import Swiper from 'swiper';
import {gsap} from '../../../vendors/gsap';
import {Draggable} from '../../../vendors/gsap/Draggable';
import createArrowBtn from '../../common/createArrowBtn/index';
import createZoomBtn from '../../common/createZoomBtn';
import createCloseBtn from '../../common/createCloseBtn';
import createLinkBtn from '../../common/createLinkBtn';
import getDOMfromString from '../../../helpers/getDOMfromString';
import updateCarouselTabIndex from '../../../helpers/updateCarouselTabIndex';
import classes from './styles.module.css';

/**
 * This method creates and return carousel
 *
 * @param {Object} config - options to customize carousel
 * @returns {Object} - next/prev slide click handlers
 */
const createMainCarousel = config => {
    const {el, storyData, onClose, onZoom, labelPrevious, labelNext, labelZoomIn, labelClose, labelFeaturedLink} =
        config;
    const data = Array.isArray(storyData) ? storyData : [];
    const slides = [];
    let container = null;
    let mainSwiper = null;

    /**
     * This function animates the next slide on carousel initialization
     */
    function initNextSlideAnimation() {
        if (slides.length < 2) return;

        gsap.fromTo(
            slides[1].querySelector('img'),
            {
                x: '100%'
            },
            {
                x: '0',
                duration: 0.7,
                ease: 'power1.out',
                delay: 0.1
            },
            '<'
        );
    }

    /**
     * This function returns a slide
     *
     * @param {Object} imgConfig - image config
     * @returns {HTMLElement} - slide
     */
    function getSlide(imgConfig) {
        const {src, alt = '', width, height, firstImage, dataSrc} = imgConfig;

        const loadingStyle = firstImage === true ? 'eager' : 'lazy';
        const fecthPriority = firstImage === true ? 'high' : 'low';

        const escapedAlt = alt.replace(/"/g, "'");

        return getDOMfromString(
            `<div class="swiper-slide" aria-hidden="true">
                <img src="${src}" alt="${escapedAlt}" width="${width}" height="${height}" loading="${loadingStyle}" fetchpriority="${fecthPriority}" data-src="${dataSrc}" class="lazy" />
            </div>`
        );
    }

    /**
     * This function handles zoom btn click
     *
     * @returns {undefined}
     */
    function zoomClickHandler() {
        if (onZoom) {
            onZoom(mainSwiper.realIndex);
        }
    }

    /**
     * This function handles close click
     *
     * @returns {undefined}
     */
    function closeClickHandler() {
        if (onClose) {
            onClose();
        }
    }

    /**
     * This function returns next/prev buttons
     *
     * @returns {Object} - next/prev btns
     */
    function getNavBtns() {
        const prevBtn = createArrowBtn({
            customClasses: [classes.prevBtn, 'prev-btn', '!bg-black-3', 'slide-icon'],
            type: 'right',
            htmlStr: true,
            label: labelPrevious
        });

        const nextBtn = createArrowBtn({
            customClasses: [classes.nextBtn, 'next-btn', '!bg-black-3', 'slide-icon'],
            type: 'left',
            htmlStr: true,
            label: labelNext
        });

        const zoomBtn = createZoomBtn({
            customClasses: [classes.zoomBtn, 'zoom-btn', '!bg-black-3', 'slide-icon'],
            htmlStr: true,
            label: labelZoomIn
        });

        const linkBtn = createLinkBtn({
            htmlStr: true,
            iconOnly: true,
            label: labelFeaturedLink
        });

        const closeBtn = createCloseBtn({
            customClasses: [classes.closeBtn, '!bg-white', 'close-btn', 'slide-icon'],
            htmlStr: true,
            label: labelClose
        });

        return {prevBtn, nextBtn, zoomBtn, linkBtn, closeBtn};
    }

    /**
     * Handles the drag functionality
     *
     * @param {Object} dragConfig - trigger element, navigation click handlers
     * @returns {Object} - draggable object
     */
    function dragHandler(dragConfig) {
        const {triggerEl = container, slideNext, slidePrev} = dragConfig;
        gsap.registerPlugin(Draggable);
        const proxy = document.createElement('div');
        return Draggable.create(proxy, {
            type: 'x',
            lockAxis: true,
            trigger: triggerEl,
            /**
             * Executed on drag start
             */
            onDragStart() {
                if (!slideNext) return;

                if (this.getDirection() === 'left') {
                    slideNext();
                } else {
                    slidePrev();
                }
            }
        });
    }

    /**
     * This function will generate the html for carousel
     */
    function generateCarousel() {
        const sliderStr = `<div class="main-carousel ${classes.mainCarousel} ${classes.full}">
        <div class="swiper w-full" id="main-carousel">
            <div class="swiper-wrapper"></div></div>
        </div>`;
        container = getDOMfromString(sliderStr);
        const wrapper = container.querySelector('.swiper-wrapper');

        data.forEach((item, index) => {
            const imageUrl = new URL(item.tileImage.src);
            const scaleWidth = 600;
            const scaleFactor = scaleWidth / item.tileImage.width;
            const scaleHeight = scaleFactor * item.tileImage.height;

            // Stories use the medium version of the image...
            imageUrl.searchParams.set('sw', scaleWidth);
            imageUrl.searchParams.set('q', 100);

            const slide = getSlide({
                src: item.tileImage.src,
                alt: item.tileImage.alt || item.name,
                width: scaleWidth,
                height: scaleHeight,
                firstImage: index === 0,
                dataSrc: imageUrl.href
            });

            slides.push(slide);
            wrapper.appendChild(slide);
        });

        const {nextBtn, prevBtn, zoomBtn, linkBtn, closeBtn} = getNavBtns();
        const ariaLabel = labelFeaturedLink.replace('{0}', storyData[0]?.models[0] ?? '');

        // 2024-08-23: Feature link is diabled for now

        const btnContainerStr = `<div class="${classes.btnContainer} ${
            storyData.length === 1 && classes.noNavArrows
        } btn-container">
            ${closeBtn}
            <div class="${classes.navContainer}  ${storyData?.length > 1 ? '' : 'hidden'}">
                ${nextBtn}
                ${prevBtn}
            </div>
            <div class="${classes.zoomContainer} zoomContainer">
                ${zoomBtn}
                <a class="${classes.linkBtn} link-btn hidden !bg-black-3 slide-icon ${
            storyData[0]?.featuredLink ? '' : 'hidden'
        }"
                aria-label="${ariaLabel}"
                title="${ariaLabel}"
                href="${storyData[0]?.featuredLink ?? '#'}"
                target="_blank">
                    ${linkBtn}
                </a>
            </div>
        </div>`;

        const btnContainer = getDOMfromString(btnContainerStr);
        btnContainer.querySelector('.close-btn').onclick = closeClickHandler;
        btnContainer.querySelector('.zoom-btn').onclick = zoomClickHandler;

        container.appendChild(btnContainer);
        el.prepend(container);
    }

    /**
     * Initialize and returns swiper object
     *
     * @returns {Object} swiper - carousel object
     */
    function initializeSwiper() {
        mainSwiper = new Swiper('#main-carousel', {
            lazy: true,
            loop: true,
            slidesPerView: 1,
            allowTouchMove: false,
            speed: 700,
            breakpoints: {
                // when window width is >= 1024px
                1024: {
                    slidesPerView: storyData.length > 2 ? 1.1 : 1
                }
            },
            watchSlidesProgress: true,
            on: {
                /**
                 * @param {Swiper} swiper - swiper instance
                 */
                afterInit: swiper => {
                    setTimeout(() => updateCarouselTabIndex(swiper, '.swiper-slide-active', false), 700);
                },
                /**
                 * @param {Swiper} swiper - swiper instance
                 */
                slideChangeTransitionEnd: swiper => {
                    updateCarouselTabIndex(swiper, '.swiper-slide-active', false);
                }
            }
        });

        return mainSwiper;
    }

    generateCarousel();
    initNextSlideAnimation();

    // initializing the swiper
    mainSwiper = initializeSwiper();

    return {
        mainSwiper,
        initNextSlideAnimation,
        mainDragHandler: dragHandler
    };
};

export default createMainCarousel;
